import { eventBus, useOnline, useTranslation } from '@libs/core';
import { SystemEventTypeEnum } from '@libs/types';
import { useEffect, useRef } from 'react';
import { EWidgetVersion } from '../types/enums';
import { ToastBarType } from '../types/toastBar.type';
import { preloadIcon } from '../widgets/Image/imageUtils';

const DEFAULT_DELAY = 3000;

/**
 * Icon of every connection-related toast (offline, slow request). Shared on purpose: this component
 * preloads it while the connection is still good, so a single cached entry covers all of them —
 * a toast about a broken/slow network must never wait for its own icon to download.
 */
export const TOAST_ICON = 'ic_alert_circle_small';
export const ARROW_RETRY_ICON = 'ic_arrow_retry';

export const OFFLINE_MESSAGE_KEY = 'no.internet.connection.message';
export const OFFLINE_MESSAGE_FALLBACK = 'You’re offline — some actions might be unavailable.';

/**
 * Builds the "you are offline" toast. Shared with the retry button of the offline popup
 * (see WidgetWrapper), so both places show the same message, icon and duration.
 */
export const buildOfflineToast = (message: string): ToastBarType => ({ message, iconName: TOAST_ICON, delay: DEFAULT_DELAY });

export const Offline = () => {
    const isOnline = useOnline();
    const { t } = useTranslation();
    const userLostInternetConnection = useRef<boolean>(false);

    // The offline toast icon can no longer be downloaded once the connection is lost, so cache it while still online.
    // Runs again on every transition back online. Toasts are not widgets, so they always use the v1 icons.
    useEffect(() => {
        if (isOnline) {
            preloadIcon(TOAST_ICON, EWidgetVersion.V1);
            preloadIcon(ARROW_RETRY_ICON, EWidgetVersion.V1);
        }
    }, [isOnline]);

    useEffect(() => {
        let toastMessage: ToastBarType | undefined;
        if (!isOnline) {
            userLostInternetConnection.current = true;
            toastMessage = buildOfflineToast(t(OFFLINE_MESSAGE_KEY, OFFLINE_MESSAGE_FALLBACK));
        } else if (userLostInternetConnection.current) {
            userLostInternetConnection.current = false;
            toastMessage = {
                message: t('internet.connection.restored.message', 'You’re online again!'),
                iconName: 'ic_check_20',
                delay: DEFAULT_DELAY,
            };
        }
        if (toastMessage) {
            eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, toastMessage);
        }
    }, [isOnline, t]);

    return false;
};
